import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBloodResults, getAlerts, getNutritionRecords, markFirstReturnSeen } from '../utils/api';
import { Activity, TrendingUp, AlertTriangle, Calendar, Camera, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import DashboardBloodGraphs from '../components/DashboardBloodGraphs';
import DashboardNutritionGraphs from '../components/DashboardNutritionGraphs';
import FirstReturnExperience from '../components/FirstReturnExperience';
import SoftBlurWrapper from '../components/SoftBlurWrapper';
import WellbeingTrendChart from '../components/WellbeingTrendChart';
import TimeWindowSelector from '../components/TimeWindowSelector';
import LymphocyteTrendCard from '../components/LymphocyteTrendCard';
import { UNITS, convertHaemoglobinForDisplay } from '../utils/unitFormatter';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import useUsageLimit from '../hooks/useUsageLimit';

const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const { isReadOnly, targetUser, readonlyToken } = useReadOnly();
  const displayUser = isReadOnly ? targetUser : user;
  const userLocale = displayUser?.country || 'AU';
  const [bloodResults, setBloodResults] = useState([]);
  const [nutritionRecords, setNutritionRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clinicalRanges, setClinicalRanges] = useState(null);
  const [activeMarker, setActiveMarker] = useState('wbc');
  const [showFirstReturn, setShowFirstReturn] = useState(false);
  const [dashboardTimeWindow, setDashboardTimeWindow] = useState('all'); // Shared time window for all dashboard charts
  
  // Usage limit for soft blur gating
  const { isFreeLimitReached, loading: usageLoading } = useUsageLimit();

  // Check if we should show the First Return experience
  useEffect(() => {
    // First-Return (Day 2) experience should show when:
    // 1. User has completed onboarding
    // 2. User hasn't seen the first return experience yet
    // 3. This is NOT the same session where they just completed onboarding
    const justCompletedOnboarding = sessionStorage.getItem('onboarding_just_completed') === 'true';
    
    if (user?.onboarding_completed && !user?.has_seen_first_return && !justCompletedOnboarding) {
      setShowFirstReturn(true);
    }
  }, [user]);

  // Handle dismissing the First Return experience
  const handleDismissFirstReturn = async () => {
    setShowFirstReturn(false);
    try {
      await markFirstReturnSeen();
      updateUser({ has_seen_first_return: true });
    } catch (error) {
      console.error('Error marking first return as seen:', error);
    }
  };

  // Transform blood results for display (convert haemoglobin for US locale)
  const transformResultsForDisplay = (rawResults) => {
    return rawResults.map(result => ({
      ...result,
      haemoglobin: result.haemoglobin !== null 
        ? convertHaemoglobinForDisplay(result.haemoglobin, userLocale)
        : null
    }));
  };

  useEffect(() => {
    fetchData();
  }, [readonlyToken]); // Re-fetch when entering/exiting readonly mode

  const fetchData = async () => {
    try {
      const [resultsData, nutritionData, alertsData, rangesData] = await Promise.all([
        getBloodResults(),
        getNutritionRecords(),
        getAlerts(),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinical-ranges`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json())
      ]);
      // Transform haemoglobin values for display based on user locale
      setBloodResults(transformResultsForDisplay(resultsData));
      setNutritionRecords(nutritionData);
      setAlerts(alertsData);
      setClinicalRanges(rangesData.ranges);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getMarkers = () => {
    if (!clinicalRanges) {
      // Default ranges using centralized unit formatter
      return [
        { key: 'wbc', label: 'WBC', color: '#0F766E', unit: UNITS.BLOOD_COUNT, range: { min: 4.0, max: 11.0 } },
        { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: UNITS.BLOOD_COUNT, range: { min: 1.0, max: 4.0 } },
        { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: UNITS.BLOOD_COUNT, range: { min: 150, max: 400 } },
        { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: UNITS.HEMOGLOBIN_SI, range: { min: 130, max: 180 } },
        { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: UNITS.BLOOD_COUNT, range: { min: 2.0, max: 7.5 } }
      ];
    }
    
    return [
      { key: 'wbc', label: 'WBC', color: '#0F766E', unit: clinicalRanges.wbc.unit, range: { min: clinicalRanges.wbc.min, max: clinicalRanges.wbc.max } },
      { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: clinicalRanges.lymphocytes.unit, range: { min: clinicalRanges.lymphocytes.min, max: clinicalRanges.lymphocytes.max } },
      { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: clinicalRanges.platelets.unit, range: { min: clinicalRanges.platelets.min, max: clinicalRanges.platelets.max } },
      { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: clinicalRanges.haemoglobin.unit, range: { min: clinicalRanges.haemoglobin.min, max: clinicalRanges.haemoglobin.max } },
      { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: clinicalRanges.neutrophils.unit, range: { min: clinicalRanges.neutrophils.min, max: clinicalRanges.neutrophils.max } }
    ];
  };

  const markers = getMarkers();

  const scrollToChart = (markerKey) => {
    const element = document.getElementById(`chart-${markerKey}`);
    if (element) {
      const yOffset = -100; // Offset to account for header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveMarker(markerKey);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      // Find which chart is currently in view
      for (const marker of markers) {
        const element = document.getElementById(`chart-${marker.key}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.pageYOffset;
          const elementBottom = elementTop + rect.height;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveMarker(marker.key);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [markers]);

  const getChartData = (markerKey) => {
    return bloodResults
      .filter(r => r[markerKey] !== null && r[markerKey] !== undefined)
      .map(r => ({
        date: r.date,
        value: r[markerKey]
      }));
  };

  const unreadAlerts = alerts.filter(a => !a.read).length;

  const getLatestValue = (marker) => {
    const latest = bloodResults.find(r => r[marker] !== null && r[marker] !== undefined);
    return latest ? latest[marker] : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* First Return (Day 2) Experience - shown once after onboarding (not in readonly mode) */}
      {showFirstReturn && !isReadOnly && !loading && (
        <FirstReturnExperience 
          userName={displayUser?.name} 
          onDismiss={handleDismissFirstReturn}
          bloodResultCount={bloodResults.length}
        />
      )}

      {/* Wellbeing Section - mood check-in + trends chart */}
      {!showFirstReturn && (
        <WellbeingTrendChart />
      )}

      {/* Lymphocyte Trend Card - shows doubling time or stable/decreasing status */}
      {!showFirstReturn && !isReadOnly && (
        <LymphocyteTrendCard />
      )}

      {/* Snap your latest results - quick action to the pathology report scanner */}
      {!showFirstReturn && !isReadOnly && (
        <Link
          to="/blood-results/add-step-by-step?scan=1"
          className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition-all"
          data-testid="snap-results-quick-action"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 rounded-xl">
              <Camera className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">Snap your latest results</p>
              <p className="text-xs text-slate-500">Scan a pathology report to add your bloods in seconds</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
        </Link>
      )}

      {/* Alert Banner */}
      {unreadAlerts > 0 && (
        <Link to="/alerts" className="block">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 hover:bg-amber-100 transition-colors cursor-pointer">
            <div className="flex items-start">
              <AlertTriangle className="h-6 w-6 text-amber-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 mb-1">
                  You have {unreadAlerts} new alert{unreadAlerts > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-amber-700">Click to view your informational alerts</p>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Stats Grid - 2 columns on mobile, responsive on larger screens */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 md:gap-6">
        {markers.map((marker) => {
          const latestValue = getLatestValue(marker.key);
          
          // Determine status based on clinical ranges
          const getStatus = (value, range) => {
            if (value === null || value === undefined || !range) return null;
            if (value < range.min) return { label: 'Low', color: 'text-blue-600', bg: 'bg-blue-50' };
            if (value > range.max) return { label: 'High', color: 'text-amber-600', bg: 'bg-amber-50' };
            return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-50' };
          };
          
          const status = getStatus(latestValue, marker.range);
          
          return (
            <div
              key={marker.key}
              className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs md:text-sm font-medium text-slate-500 uppercase tracking-wider truncate">{marker.label}</p>
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: marker.color }} />
              </div>
              <div className="flex items-baseline gap-1.5 sm:gap-2">
                <p className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900">
                  {latestValue !== null ? latestValue.toFixed(1) : '—'}
                </p>
                {status && (
                  <span className={`text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">Latest reading</p>
            </div>
          );
        })}
      </div>

      {/* All Graphs Section - No Selectors, All Visible */}
      {/* Charts Section - Wrapped with Soft Blur for Free Plan Gating */}
      <SoftBlurWrapper isBlurred={isFreeLimitReached && !usageLoading}>
        <div className="space-y-8">
          {/* Wrapper for Blood + Nutrition with shared sticky time selector on mobile */}
          <div className="relative">
            {/* Mobile: Sticky time selector that covers both Blood and Nutrition sections */}
            <div 
              className="lg:hidden sticky z-40 bg-white"
              style={{ top: '44px' }}
            >
              <div className="flex justify-end px-4 py-2.5 border-b border-slate-200 shadow-sm">
                <TimeWindowSelector selected={dashboardTimeWindow} onChange={setDashboardTimeWindow} />
              </div>
            </div>

            {/* Blood Results Trends - ALL GRAPHS STACKED */}
            {bloodResults.length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-slate-200">
                {/* Mobile: Title only (selector moved to shared sticky above) */}
                <div className="lg:hidden px-4 pt-4 pb-2">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Blood Results</h2>
                  <p className="text-xs text-slate-600">Core blood markers relevant to monitoring disease activity and overall blood health.</p>
                </div>
                
                {/* Desktop: Sticky header with title + selector side by side */}
                <div 
                  className="hidden lg:block sticky z-40 bg-white rounded-t-2xl border-b border-slate-100"
                  style={{ top: '0px' }}
                >
                  <div className="px-6 pt-6 pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">Blood Results</h2>
                        <p className="text-sm text-slate-600">Core blood markers relevant to monitoring disease activity and overall blood health.</p>
                      </div>
                      <div className="flex-shrink-0">
                        <TimeWindowSelector selected={dashboardTimeWindow} onChange={setDashboardTimeWindow} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="-mx-4 md:mx-0 px-0 pb-4 md:px-3 md:pb-6">
                  <DashboardBloodGraphs results={bloodResults} clinicalRanges={clinicalRanges} timeWindow={dashboardTimeWindow} />
                </div>
              </div>
            )}

            {/* Nutrition Trends - ALL GRAPHS STACKED (Visually Distinct) */}
            {nutritionRecords.length > 0 && (
              <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-200 mt-6">
                <div className="px-4 pt-4 md:px-6 md:pt-6 pb-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 mb-2">Nutrition & Deficiencies</h2>
                  <p className="text-xs md:text-sm text-emerald-700">Supportive biomarkers influenced by diet, absorption, and supplementation.</p>
                </div>
                <div className="-mx-4 md:mx-0 px-0 pb-4 md:px-3 md:pb-6">
                  <DashboardNutritionGraphs records={nutritionRecords} timeWindow={dashboardTimeWindow} />
                </div>
              </div>
            )}
          </div>
          {/* End of Blood + Nutrition wrapper */}

          {/* No Data Message */}
          {bloodResults.length === 0 && nutritionRecords.length === 0 && (
            <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm text-center">
              <Activity className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No trends yet</h3>
              <p className="text-slate-600">Add blood results or nutrition records to see trends</p>
            </div>
          )}
        </div>
      </SoftBlurWrapper>

      <div className="grid md:grid-cols-3 gap-6">
        <Link to="/blood-results" className="card-interactive group" data-testid="quick-action-blood">
          <Activity className="h-8 w-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Add Blood Results</h3>
          <p className="text-slate-600">Record your latest test results</p>
        </Link>
        
        <Link to="/symptoms" className="card-interactive group" data-testid="quick-action-symptoms">
          <Calendar className="h-8 w-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Track Symptoms</h3>
          <p className="text-slate-600">Log today&apos;s symptoms</p>
        </Link>
        
        <Link to="/export" className="card-interactive group" data-testid="quick-action-export">
          <TrendingUp className="h-8 w-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Export Report</h3>
          <p className="text-slate-600">Generate PDF for your doctor</p>
        </Link>
      </div>
    </div>
  );
};

export default DashboardPage;
